import React, { useState, useEffect } from 'react';
import api from '../api/client';
import FlexibleSecondaryTable from './FlexibleSecondaryTable';
import Pagination from './TablePagination';
import '@fortawesome/fontawesome-free/css/all.min.css';

const SORT_ORDER = {
    NONE: 'NONE',
    ASC: 'ASC',
    DESC: 'DESC'
};

const UniversalDataTable = ({
    columns,
    endpoint,
    secondaryTableConfig,
    keyFieldsConfig,
    tableName,
    onEdit,
    onDelete,
    searchTerm,
    filterParams,
    enableEdit = true,
    enableDelete = true,
    updateKey,
    showCompletedShipments = false,
    toggleCompletedShipments
}) => {
    const [expandedRows, setExpandedRows] = useState({});
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: '', order: 'ASC' });
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(1000);
    const [totalItems, setTotalItems] = useState(0);

    const primaryKeyConfig = keyFieldsConfig[tableName];
    const primaryKey = Array.isArray(primaryKeyConfig) ? primaryKeyConfig[0] : primaryKeyConfig;

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            order: prev.key === key && prev.order === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC,
        }));
    };

    useEffect(() => {
        console.log('showCompletedShipments:', showCompletedShipments);
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await api.get(endpoint, {
                    params: { page, limit, search: searchTerm, ...filterParams, sortKey: sortConfig.key, sortOrder: sortConfig.order }
                });
                const responseData = response.data;
                console.log('API Response:', responseData);
                if (responseData && Array.isArray(responseData.data)) {
                    console.log('First Row Sample:', responseData.data[0]);
                    const filteredData = tableName === 'amz_shipments' && !showCompletedShipments
                        ? responseData.data.filter(row => row.statusName !== 'Complete') // Try row.statusName !== 'Complete' if statusID doesn't work
                        : responseData.data;
                    console.log('Filtered Data:', filteredData);
                    setData(filteredData);
                    setTotalItems(responseData.total || 0);
                } else {
                    setError('Unexpected response format');
                }
            } catch (err) {
                setError('Failed to fetch data');
                console.error('Fetch Error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [searchTerm, filterParams, sortConfig, page, limit, updateKey, showCompletedShipments]);

    const toggleExpandRow = (rowId) => {
        setExpandedRows((prev) => ({
            ...prev,
            [rowId]: !prev[rowId],
        }));
    };

    const getQueryParams = (row, primaryKeyConfig) => {
        if (Array.isArray(primaryKeyConfig)) {
            return primaryKeyConfig.map(key => `${key}=${row[key]}`).join('&');
        }
        return `${primaryKeyConfig}=${row[primaryKeyConfig]}`;
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;
    if (!data.length) return <div>No data available</div>;

    const totalPages = Math.ceil(totalItems / limit);

    return (
        <div className="expandable-table">
            <table className="table">
                <thead className="table__heading">
                    <tr>
                        <th></th>
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                onClick={() => handleSort(col.key)}
                                style={{ cursor: 'pointer' }}
                            >
                                {col.label}
                                {sortConfig.key === col.key && (
                                    <svg
                                        className={`icon icon__sort ${sortConfig.order === SORT_ORDER.ASC ? 'icon__sort--asc' : 'icon__sort--desc'}`}
                                        width="16"
                                        height="16"
                                    >
                                        <use xlinkHref="#sort" />
                                    </svg>
                                )}
                            </th>
                        ))}
                        {(enableEdit || enableDelete) && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row) => (
                        <React.Fragment key={row.SKU}>
                            <tr>
                                <td>
                                    <button
                                        className="table__expand-button"
                                        onClick={() => toggleExpandRow(row[primaryKey])}
                                    >
                                        {expandedRows[row[primaryKey]] ? '▲' : '▼'}
                                    </button>
                                </td>
                                {columns.map((col) => (
                                    <td key={col.key}>
                                        {col.key === 'ShipmentID' && row.proof_needed ? (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                {row[col.key] ?? '-'}
                                                <span
                                                    title="Proof of delivery required"
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                                        padding: '2px 7px', borderRadius: 20,
                                                        background: '#fef3c7', border: '1px solid #fde68a',
                                                        fontSize: 11, fontWeight: 600, color: '#92400e',
                                                        whiteSpace: 'nowrap', lineHeight: 1.6
                                                    }}
                                                >
                                                    <i className="fas fa-triangle-exclamation" style={{ fontSize: 10 }} />
                                                    Proof required
                                                </span>
                                            </span>
                                        ) : (
                                            row[col.key] ?? '-'
                                        )}
                                    </td>
                                ))}
                                {(enableEdit || enableDelete) && (
                                    <td className="table__actions">
                                        {enableEdit && (
                                            <i
                                                className="fa fa-edit"
                                                onClick={() => {
                                                    const keyFields = keyFieldsConfig[tableName];
                                                    const id = Array.isArray(keyFields)
                                                        ? keyFields.map(key => row[key]).join('-')
                                                        : row[keyFields];
                                                    onEdit(row, id);
                                                }}
                                                style={{ cursor: 'pointer', marginRight: '10px' }}
                                            ></i>
                                        )}
                                        {enableDelete && (
                                            <i
                                                className="fa fa-trash"
                                                onClick={() => onDelete(row[primaryKey])}
                                                style={{ cursor: 'pointer'}}
                                            ></i>
                                        )}
                                    </td>
                                )}
                            </tr>
                            {expandedRows[row[primaryKey]] && (
                                <tr>
                                    <td colSpan={columns.length + 2}>
                                        <FlexibleSecondaryTable
                                            parentRowId={row[primaryKey]}
                                            endpoint={`${secondaryTableConfig.endpointBase}?${getQueryParams(row, primaryKeyConfig)}`}
                                            columns={secondaryTableConfig.columns}
                                            keyFieldsConfig={keyFieldsConfig}
                                            enableEdit={false}
                                            enableDelete={false}
                                            showHistory={false}
                                        />
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            <Pagination
                page={page}
                totalPages={totalPages}
                limit={limit}
                setPage={setPage}
                setLimit={setLimit}
            />
        </div>
    );
};

export default UniversalDataTable;